import React, { useState } from 'react';
import { sortDayKeysDesc, formatDayLabel } from '../../photoUpload/dayBuckets';
import { trackEvent } from '../../analytics';
import { hasLargerStoragePoolThan } from '../../pricing/plans';
import UpgradePromptDialog from '../UpgradePromptDialog';
import { UPSELL_PROMPTS } from '../../upsell/triggers';
import {
  formatBytes,
  formatDuration,
  ASSUMED_UPLOAD_BPS,
  RETENTION_DAYS,
  RETENTION_WARNING_LEAD_DAYS,
} from '../../photoUpload/caps';
import { RETENTION_FREE_30D } from '../../entitlements/evaluate';

// Preflight / resume dialog. Renders two variants:
//   - "new" — first drop, shows day strip, ETA, caps meter, skip warnings
//   - "resume" — some photos already uploaded → shows how many will be
//     skipped and how many remain
//
// Both share the same modal chrome. Which variant is decided by whether
// `dedup.totals.alreadyUploadedCount` > 0.

const CoverageStrip = ({ dayBuckets }) => {
  if (!dayBuckets) return null;
  const keys = sortDayKeysDesc([...dayBuckets.keys()]);
  const knownKeys = keys.filter((k) => k !== 'unknown');
  return (
    <div className="pi-coverage">
      {knownKeys.map((k) => (
        <div key={k} className="pi-day-cell" title={formatDayLabel(k)} />
      ))}
      {keys.includes('unknown') && (
        <div className="pi-day-cell pi-day-cell--gap" title="Unknown date" />
      )}
    </div>
  );
};

const PhotoUploadDialog = ({
  phase,
  scan,
  dedup,
  caps,
  retentionClass,
  onCancel,
  onConfirm,
  // Effective tier from useEntitlements, for analytics attribution only — the
  // upgrade gate reads the effective pool off `caps`, not this. Defaults to
  // Free to match the hook's own missing-doc semantics.
  tier = 'free',
  // #350 — full entitlements + raw Stripe state, so the pool-cap CTA can open
  // the shared upgrade prompt (naming the real next tier, routing existing
  // subscribers to the portal) instead of only linking out to /pricing.
  entitlements,
  subscription,
}) => {
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  if (phase !== 'preflight' || !scan || !dedup || !caps) return null;

  const isResume = dedup.totals.alreadyUploadedCount > 0;
  const toUploadCount = dedup.totals.toUploadCount;
  const toUploadBytes = dedup.totals.toUploadBytes;
  const dayCount = scan.totals.dayCount;
  const skippedCount = scan.skippedNonImages.length;
  const missingExifCount = scan.missingExif;
  const overCap = caps.blocked;
  // A bigger pool has to actually be purchasable, or "upgrade your plan" sells
  // a fix that doesn't exist (Pro is the top self-serve tier). Gate on the pool
  // they're actually blocked against, so a grandfathered allowance above the
  // catalog isn't pitched a "bigger" tier that would shrink it.
  const canUpgradePool = hasLargerStoragePoolThan(caps.poolTotalBytes);
  const nothingToUpload = toUploadCount === 0 && !overCap;

  const etaSeconds = ASSUMED_UPLOAD_BPS > 0 ? toUploadBytes / ASSUMED_UPLOAD_BPS : null;

  // Free accounts learn the retention rule here, at upload — not when the
  // sweep has already taken the photos (#345).
  const photosExpire = retentionClass === RETENTION_FREE_30D;

  const dayKeys = sortDayKeysDesc([...scan.dayBuckets.keys()].filter((k) => k !== 'unknown'));
  const dayRangeLabel = dayKeys.length > 0
    ? `${formatDayLabel(dayKeys[dayKeys.length - 1])} – ${formatDayLabel(dayKeys[0])}`
    : null;

  return (
    <div className="pi-overlay" role="dialog" aria-modal="true">
      <div className="pi-modal">
        <h2 className="pi-modal-title">
          {isResume
            ? `Resume upload — ${toUploadCount.toLocaleString()} photos left`
            : `${scan.totals.photoCount.toLocaleString()} photos ready to upload`}
        </h2>
        <div className="pi-modal-sub">
          {isResume
            ? `${dedup.totals.alreadyUploadedCount.toLocaleString()} photos are already in this project and will be skipped.`
            : `${formatBytes(toUploadBytes)} across ${dayCount} day${dayCount === 1 ? '' : 's'}.`}
        </div>

        {!isResume && dayCount > 0 && (
          <div className="pi-modal-section">
            <h4 className="pi-section-head">
              Capture days detected{dayRangeLabel ? ` · ${dayRangeLabel}` : ''}
            </h4>
            <CoverageStrip dayBuckets={scan.dayBuckets} />
          </div>
        )}

        <div className="pi-modal-section">
          {isResume && (
            <div className="pi-stat-row">
              <span className="pi-k">Already in this project (skipped)</span>
              <span className="pi-v">{dedup.totals.alreadyUploadedCount.toLocaleString()} photos</span>
            </div>
          )}
          <div className="pi-stat-row">
            <span className="pi-k">{isResume ? 'Left to upload' : 'Photos to upload'}</span>
            <span className="pi-v">
              {toUploadCount.toLocaleString()} photo{toUploadCount === 1 ? '' : 's'} · {formatBytes(toUploadBytes)}
            </span>
          </div>
          {etaSeconds != null && toUploadCount > 0 && (
            <div className="pi-stat-row">
              <span className="pi-k">Estimated upload time (assumes 20 Mbps up)</span>
              <span className="pi-v">{formatDuration(etaSeconds)}</span>
            </div>
          )}
          {caps.hasPool && (
            <div className={`pi-stat-row${caps.overPoolCap ? ' pi-stat-row--warn' : ''}`}>
              <span className="pi-k">Account storage after upload</span>
              <span className="pi-v">
                {caps.poolPending
                  ? `calculating… of ${formatBytes(caps.poolTotalBytes)}`
                  : `${formatBytes(caps.projectedPoolBytes)} of ${formatBytes(caps.poolTotalBytes)}`}
              </span>
            </div>
          )}
          {/* Informational only since #342: a project has no ceiling of its
              own, so this is a running count, not "N of a cap". The one limit
              is the pooled account row above. */}
          <div className="pi-stat-row">
            <span className="pi-k">Photos in this project after upload</span>
            <span className="pi-v">
              {caps.projectedPhotos.toLocaleString()} photo{caps.projectedPhotos === 1 ? '' : 's'}
            </span>
          </div>
        </div>

        {photosExpire && toUploadCount > 0 && (
          <div className="pi-modal-section pi-modal-note" data-testid="retention-note">
            Raw photos are removed after {RETENTION_DAYS} days without activity on this project —
            your compiled videos are always kept. Any upload or render resets the clock, and we
            email you {RETENTION_WARNING_LEAD_DAYS} days before anything is removed. On a paid
            plan raw photos stay for as long as the subscription is active.
          </div>
        )}

        {(skippedCount > 0 || missingExifCount > 0) && (
          <div className="pi-modal-section">
            {skippedCount > 0 && (
              <div className="pi-warn-line">
                {skippedCount} file{skippedCount === 1 ? '' : 's'} skipped — not photos.
              </div>
            )}
            {missingExifCount > 0 && (
              <div className="pi-warn-line">
                {missingExifCount === 1
                  ? "1 photo has no capture time — it'll be placed using its file date."
                  : `${missingExifCount} photos have no capture time — they'll be placed using their file date.`}
              </div>
            )}
          </div>
        )}

        {overCap && (
          <div className="pi-modal-section pi-modal-error">
            {caps.poolPending && (
              <div>
                Checking your account storage… we’ll enable the upload as soon as we know how much
                pooled space you have left.
              </div>
            )}
            {caps.overPoolCap && (
              <div>
                <p className="pi-error-text">
                  This upload would exceed your plan’s {formatBytes(caps.poolTotalBytes)} of pooled account storage
                  ({formatBytes(caps.projectedPoolBytes)} projected across all your projects).{' '}
                  {canUpgradePool
                    ? 'Free up space or upgrade your plan to add more.'
                    : 'Free up space by removing photos or projects you no longer need.'}
                </p>
                {/* #350 extends the PR #371 CTA: it opens the shared upgrade
                    prompt in place rather than navigating to /pricing in a new
                    tab. Same reason the old one used target="_blank" — the
                    drop's preflight scan must survive the detour — but now the
                    prompt names the actual next tier, shows the pool meter, and
                    emits the funnel events. useEntitlements is a live listener,
                    so this dialog still unblocks itself once the upgrade lands.
                    The original `cta_click` fires unchanged so the #315
                    dashboards keep their series. */}
                {canUpgradePool && (
                  <button
                    type="button"
                    className="pi-upgrade-cta"
                    data-testid="pool-cap-upgrade-cta"
                    onClick={() => {
                      trackEvent('cta_click', {
                        cta_id: 'upload_pool_cap_upgrade',
                        // The dialog only exists inside a signed-in project view.
                        signed_in: true,
                        tier,
                      });
                      setShowUpgradePrompt(true);
                    }}
                  >
                    Upgrade your plan <span aria-hidden="true">&rarr;</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {nothingToUpload && (
          <div className="pi-modal-section pi-modal-note">
            Nothing new to upload — every photo you picked is already in this project.
          </div>
        )}

        <div className="pi-modal-foot">
          <div className="pi-note">
            Keep this tab open while uploading. If it stops, upload the same folder again — we'll skip whatever already made it.
          </div>
          <button type="button" className="btn ghost pi-btn" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="btn btn-teal pi-btn"
            onClick={onConfirm}
            disabled={overCap || nothingToUpload}
          >
            {isResume ? 'Resume upload' : 'Start upload'}
          </button>
        </div>

        {showUpgradePrompt && (
          <UpgradePromptDialog
            promptId={UPSELL_PROMPTS.STORAGE_POOL_FULL}
            entitlements={entitlements}
            subscription={subscription}
            // Current pool usage, matching the account page's meter. The
            // projected-after-upload figure is already in the stat row above; a
            // meter is the wrong shape for a number that exceeds its own track.
            usedBytes={caps.poolUsedBytes}
            totalBytes={caps.poolTotalBytes}
            // "Free up space" needs somewhere to go: dismiss the preflight so
            // the user is back in the project, where photos and projects can be
            // removed. Re-dropping the same folder resumes and skips whatever
            // already uploaded.
            onFreeUpSpace={() => { setShowUpgradePrompt(false); onCancel(); }}
            onClose={() => setShowUpgradePrompt(false)}
          />
        )}
      </div>
    </div>
  );
};

export default PhotoUploadDialog;
